import { useState, useEffect, useRef } from "react";
import useWindowSize from "../hooks/useWindowSize";
import aitLogo from "../assets/ait.svg";
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import '../styles/pages/ForgotPasswordPage.css';

/* ═══════════════════════ ICON COMPONENTS ═══════════════════════ */
const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="4" width="20" height="16" rx="3"/>
    <polyline points="22,7 12,14 2,7"/>
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="11" width="18" height="11" rx="3"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const EyeOpen = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeClosed = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

/* ═══════════════════════ STEP INDICATOR ═══════════════════════ */
const StepIndicator = ({ current, total }) => (
  <div className="forgot-password-stepper">
    {Array.from({ length: total }, (_, i) => (
      <div 
        key={i} 
        className={`forgot-password-stepper__dot ${
          i + 1 === current ? 'forgot-password-stepper__dot--active' : 
          i + 1 < current ? 'forgot-password-stepper__dot--completed' : ''
        }`}
      />
    ))}
  </div>
);

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */
export default function ForgotPasswordPage({ onBackToLogin }) {
  const { isMobile } = useWindowSize();
  const [step, setStep]             = useState(1);     // 1=email, 2=OTP, 3=newPw, 4=success
  const [email, setEmail]           = useState("");
  const [emailErr, setEmailErr]     = useState("");
  const [otp, setOtp]               = useState(["","","","","",""]);
  const [otpErr, setOtpErr]         = useState("");
  const [newPw, setNewPw]           = useState("");
  const [confirmPw, setConfirmPw]   = useState("");
  const [showNewPw, setShowNewPw]   = useState(false);
  const [showConfPw, setShowConfPw] = useState(false);
  const [newPwErr, setNewPwErr]     = useState("");
  const [confPwErr, setConfPwErr]   = useState("");
  const [loading, setLoading]       = useState(false);
  const [formMsg, setFormMsg]       = useState({ type: "", text: "" });
  const [timer, setTimer]           = useState(0);

  const otpRefs = useRef([]);

  /* countdown timer for resend */
  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  /* ── STEP 1 — Email ── */
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setEmailErr("Email is required"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailErr("Enter a valid email"); return; }

    setLoading(true);
    setFormMsg({ type: "", text: "" });

    try {
      await resetPassword({ username: email.trim().toLowerCase() });
      
      setLoading(false);
      setTimer(60);
      setStep(2);
      setFormMsg({ type: "success", text: `Verification code sent to ${email}` });
    } catch (error) {
      setLoading(false);
      console.error('Password reset error:', error);
      
      if (error.name === 'UserNotFoundException') {
        setEmailErr("No account found with this email address");
      } else if (error.name === 'LimitExceededException') {
        setEmailErr("Too many attempts. Please try again later.");
      } else {
        setEmailErr(error.message || "Failed to send verification code");
      }
    }
  };

  /* ── STEP 2 — OTP ── */
  const handleOtpChange = (idx, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    setOtpErr("");
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKey = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = [...otp];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || "";
    setOtp(next);
    setOtpErr("");
    const focusIdx = Math.min(pasted.length, 5);
    otpRefs.current[focusIdx]?.focus();
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) { setOtpErr("Please enter the full 6-digit code"); return; }

    setStep(3);
  };

  const handleResend = async () => {
    if (timer > 0) return;
    
    try {
      await resetPassword({ username: email.trim().toLowerCase() });
      setOtp(["","","","","",""]);
      setOtpErr("");
      setTimer(60);
      setFormMsg({ type: "success", text: "A new verification code has been sent" });
    } catch (error) {
      console.error('Resend error:', error);
      setFormMsg({ type: "error", text: "Failed to resend code. Please try again." });
    }
  };

  /* ── STEP 3 — New password ── */
  const handleNewPwSubmit = async (e) => {
    e.preventDefault();
    let hasErr = false;

    if (!newPw) { setNewPwErr("New password is required"); hasErr = true; }
    else if (newPw.length < 8) { setNewPwErr("Password must be at least 8 characters"); hasErr = true; }
    else if (!/[A-Z]/.test(newPw)) { setNewPwErr("Include at least one uppercase letter"); hasErr = true; }
    else if (!/[a-z]/.test(newPw)) { setNewPwErr("Include at least one lowercase letter"); hasErr = true; }
    else if (!/[0-9]/.test(newPw)) { setNewPwErr("Include at least one number"); hasErr = true; }
    else if (!/[^A-Za-z0-9]/.test(newPw)) { setNewPwErr("Include at least one special character"); hasErr = true; }
    else setNewPwErr("");

    if (!confirmPw) { setConfPwErr("Please confirm your password"); hasErr = true; }
    else if (confirmPw !== newPw) { setConfPwErr("Passwords do not match"); hasErr = true; }
    else setConfPwErr("");

    if (hasErr) return;

    setLoading(true);
    setFormMsg({ type: "", text: "" });

    try {
      const code = otp.join("");
      await confirmResetPassword({
        username: email.trim().toLowerCase(),
        confirmationCode: code,
        newPassword: newPw
      });
      
      setLoading(false);
      setStep(4);
    } catch (error) {
      setLoading(false);
      console.error('Password reset confirmation error:', error);
      
      if (error.name === 'CodeMismatchException') {
        setFormMsg({ type: "error", text: "Invalid verification code. Please try again." });
        setStep(2);
      } else if (error.name === 'ExpiredCodeException') {
        setFormMsg({ type: "error", text: "Verification code expired. Please request a new one." });
        setStep(1);
      } else if (error.name === 'InvalidPasswordException') {
        setNewPwErr("Password does not meet requirements");
      } else {
        setFormMsg({ type: "error", text: error.message || "Failed to reset password" });
      }
    }
  };

  /* ── Password strength calculation ── */
  const getStrength = (pw) => {
    if (!pw) return { pct: 0, label: "", color: "#e5e7eb" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { pct: 20, label: "Weak",     color: "#dc2626" };
    if (score <= 3) return { pct: 40, label: "Fair",     color: "#f97316" };
    if (score <= 4) return { pct: 60, label: "Good",     color: "#eab308" };
    if (score <= 5) return { pct: 80, label: "Strong",   color: "#22c55e" };
    return              { pct: 100, label: "Excellent", color: "#16a34a" };
  };

  const strength = getStrength(newPw);

  /* ═══════════════════════ RENDER ═══════════════════════ */
  return (
    <div className="forgot-password-page">
      <div className={`forgot-password-card ${isMobile ? 'mobile' : ''}`}>
        <img 
          src={aitLogo} 
          alt="AI Tensors" 
          className={`forgot-password-logo ${isMobile ? 'mobile' : ''}`}
        />

        {step < 4 && <StepIndicator current={step} total={3} />}

        {/* ── Message banner ── */}
        {formMsg.text && (
          <div className={`forgot-password-message forgot-password-message--${formMsg.type}`}>
            {formMsg.text}
          </div>
        )}

        {/* ════════════ STEP 1: Email ════════════ */}
        {step === 1 && (
          <form onSubmit={handleEmailSubmit} className="forgot-password-form">
            <h1 className="forgot-password-heading">Forgot Password?</h1>
            <p className="forgot-password-subheading">
              Enter your registered email address and we'll send you a verification code to reset your password.
            </p>

            <label className="forgot-password-label">Email Address</label>
            <div className="forgot-password-input-wrapper">
              <div className="forgot-password-input-icon">
                <MailIcon />
              </div>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => { setEmail(e.target.value); setEmailErr(""); }}
                className={`forgot-password-input ${emailErr ? 'error' : ''}`}
              />
              {emailErr && <p className="forgot-password-field-error">{emailErr}</p>}
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="forgot-password-button-primary"
            >
              {loading ? "Verifying..." : "Send Verification Code"}
            </button>

            <button 
              type="button" 
              onClick={onBackToLogin} 
              className="forgot-password-button-secondary"
            >
              Back to Sign In
            </button>
          </form>
        )}

        {/* ════════════ STEP 2: OTP ════════════ */}
        {step === 2 && (
          <form onSubmit={handleOtpSubmit} className="forgot-password-form">
            <h1 className="forgot-password-heading">Enter Verification Code</h1>
            <p className="forgot-password-subheading">
              We sent a 6-digit code to <strong>{email}</strong>. Please check your email.
            </p>

            {/* OTP boxes */}
            <div 
              className={`forgot-password-otp-container ${isMobile ? 'mobile' : ''}`}
              onPaste={handleOtpPaste}
            >
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKey(i, e)}
                  className={`forgot-password-otp-digit ${isMobile ? 'mobile' : ''} ${
                    otpErr ? 'error' : d ? 'filled' : ''
                  }`}
                />
              ))}
            </div>
            {otpErr && <p className="forgot-password-field-error" style={{ textAlign: 'center', marginBottom: '8px' }}>{otpErr}</p>}

            {/* Resend */}
            <p className="forgot-password-resend-text">
              {timer > 0 ? (
                <>Resend code in <strong>{timer}s</strong></>
              ) : (
                <span onClick={handleResend} className="forgot-password-resend-link">
                  Resend Code
                </span>
              )}
            </p>

            <button 
              type="submit" 
              disabled={loading}
              className="forgot-password-button-primary"
            >
              {loading ? "Verifying..." : "Verify Code"}
            </button>

            <button 
              type="button" 
              onClick={() => { setStep(1); setFormMsg({ type: "", text: "" }); }} 
              className="forgot-password-button-secondary"
            >
              Change Email
            </button>
          </form>
        )}

        {/* ════════════ STEP 3: New Password ════════════ */}
        {step === 3 && (
          <form onSubmit={handleNewPwSubmit} className="forgot-password-form">
            <h1 className="forgot-password-heading">Create New Password</h1>
            <p className="forgot-password-subheading">
              Your new password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.
            </p>

            {/* New Password */}
            <label className="forgot-password-label">New Password</label>
            <div className="forgot-password-input-wrapper">
              <div className="forgot-password-input-icon">
                <LockIcon />
              </div>
              <input
                type={showNewPw ? "text" : "password"}
                placeholder="Enter new password"
                value={newPw}
                onChange={e => { setNewPw(e.target.value); setNewPwErr(""); }}
                className={`forgot-password-input ${newPwErr ? 'error' : ''}`}
              />
              <button 
                type="button" 
                onClick={() => setShowNewPw(s => !s)}
                className={`forgot-password-toggle ${newPwErr ? 'with-error' : ''}`}
              >
                {showNewPw ? <EyeOpen /> : <EyeClosed />}
              </button>
              {newPwErr && <p className="forgot-password-field-error">{newPwErr}</p>}
            </div>

            {/* Strength bar */}
            {newPw && (
              <div className="forgot-password-strength-container">
                <div className="forgot-password-strength-bar">
                  <div 
                    className="forgot-password-strength-fill"
                    style={{
                      width: `${strength.pct}%`,
                      background: strength.color
                    }}
                  />
                </div>
                <p 
                  className="forgot-password-strength-label"
                  style={{ color: strength.color }}
                >
                  {strength.label}
                </p>
              </div>
            )}

            {/* Confirm Password */}
            <label className="forgot-password-label">Confirm Password</label>
            <div className="forgot-password-input-wrapper">
              <div className="forgot-password-input-icon">
                <ShieldIcon />
              </div>
              <input
                type={showConfPw ? "text" : "password"}
                placeholder="Re-enter new password"
                value={confirmPw}
                onChange={e => { setConfirmPw(e.target.value); setConfPwErr(""); }}
                className={`forgot-password-input ${confPwErr ? 'error' : ''}`}
              />
              <button 
                type="button" 
                onClick={() => setShowConfPw(s => !s)}
                className={`forgot-password-toggle ${confPwErr ? 'with-error' : ''}`}
              >
                {showConfPw ? <EyeOpen /> : <EyeClosed />}
              </button>
              {confPwErr && <p className="forgot-password-field-error">{confPwErr}</p>}
            </div>

            <button 
              type="submit" 
              disabled={loading}
              className="forgot-password-button-primary"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}

        {/* ════════════ STEP 4: Success ════════════ */}
        {step === 4 && (
          <div className="forgot-password-success">
            <div className="forgot-password-success-icon">
              <CheckCircleIcon />
            </div>
            <h1 className="forgot-password-heading">Password Reset Successful</h1>
            <p className="forgot-password-subheading">
              Your password has been updated successfully.<br />
              You can now sign in with your new password.
            </p>

            <button 
              type="button" 
              onClick={onBackToLogin}
              className="forgot-password-button-primary"
            >
              Back to Sign In
            </button>
          </div>
        )}

        {/* Footer */}
        <p className="forgot-password-footer">
          &copy; {new Date().getFullYear()} AI Tensors &mdash; Medical Monitoring Platform
        </p>
      </div>
    </div>
  );
}
